import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Link2, Scale, AlertTriangle, FileSignature, IdCard } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { STATUS, errMsg } from "./legalUtils";

const Sec = ({ icon: Icon, title, n, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-3">
    <h4 className="mb-2 flex items-center gap-1.5 text-sm font-extrabold text-slate-700"><Icon className="h-4 w-4 text-amber-600" /> {title} <span className="text-slate-400">({n})</span></h4>
    {n ? children : <p className="text-xs text-slate-400">لا يوجد</p>}
  </section>
);

export const LegalDossier = ({ c, onChanged, onOpenCase }) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (c.employee_id) api.legalPerson(c.employee_id).then(setD).catch((e) => toast.error(errMsg(e))); }, [c.employee_id]);
  if (!c.employee_id) return <p className="py-8 text-center text-sm text-slate-400">لا يوجد موظف مرتبط بهذه الحالة</p>;
  if (!d) return <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const attach = async (file_id, name) => {
    try { await api.legalAct(c.id, "attachments", { file_id, name, category: "وثائق المخالف" }); toast.success("أُرفقت بالحالة"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const e = d.employee;
  const others = d.cases.filter((x) => x.id !== c.id);
  return (
    <div className="space-y-3" data-testid="legal-dossier">
      <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-2xl bg-[#0f1f3d] p-4 text-sm text-white">
        <b className="text-lg text-amber-200">{e.name}</b><span>الهوية: {e.iqama_number || "—"}</span><span>الجنسية: {e.nationality || "—"}</span>
        <span>المهنة: {e.job_title || e.profession || "—"}</span><span>الفرع: {e.branch || "—"}</span><span>الحالة: {e.employee_status || "—"}</span>
        {others.length >= 2 && <span className="flex items-center gap-1 rounded-full bg-rose-500/90 px-2 text-xs font-bold"><AlertTriangle className="h-3.5 w-3.5" /> سوابق متكررة ({others.length})</span>}
      </div>
      <Sec icon={IdCard} title="وثائق الموظف" n={d.documents.length}>
        <div className="grid gap-2 sm:grid-cols-2">{d.documents.map((x) => (
          <div key={x.file_id} className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm">
            <a href={fileUrl(x.file_id)} target="_blank" rel="noreferrer" className="flex-1 truncate font-semibold text-blue-700">{x.label}</a>
            {x.expiry && <span className="text-[10px] text-slate-400">ينتهي {x.expiry}</span>}
            {c.can_act && <button onClick={() => attach(x.file_id, x.label)} data-testid={`legal-dossier-attach-${x.file_id}`} className="flex items-center gap-1 text-[11px] font-bold text-amber-700"><Link2 className="h-3 w-3" /> إرفاق</button>}
          </div>))}</div>
      </Sec>
      <Sec icon={Scale} title="قضايا وحالات أخرى" n={others.length}>
        {others.map((x) => <button key={x.id} onClick={() => onOpenCase(x.id)} className="flex w-full items-center gap-2 border-b border-slate-100 py-1.5 text-right text-sm last:border-0" data-testid={`legal-dossier-case-${x.id}`}>
          <span className="font-mono text-xs" dir="ltr">{x.number}</span><span className="flex-1 truncate">{x.title}</span>
          <span className={`rounded-full px-2 text-[10px] font-bold ${STATUS[x.status]?.[1]}`}>{x.result || STATUS[x.status]?.[0]}</span></button>)}
      </Sec>
      <Sec icon={AlertTriangle} title="المخالفات" n={d.violations.length}>
        {d.violations.map((v) => <div key={v.id} className="flex gap-3 border-b border-slate-100 py-1 text-xs last:border-0"><span>{v.violation_date}</span><span className="flex-1">{v.type || v.violation_number}</span><b>{v.amount} ر.س</b><span className="text-slate-400">{v.status}</span></div>)}
      </Sec>
      <Sec icon={FileSignature} title="القرارات الإدارية" n={d.decisions.length}>
        {d.decisions.map((x) => <div key={x.id} className="flex gap-3 border-b border-slate-100 py-1 text-xs last:border-0"><span className="font-mono">{x.number}</span><span className="flex-1">{x.title}</span><span>{x.date}</span>
          {x.archived_file_id && <a href={fileUrl(x.archived_file_id)} target="_blank" rel="noreferrer" className="text-blue-700">PDF</a>}</div>)}
      </Sec>
    </div>
  );
};
